import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { AmountInput, Button, CurrenciesUSD, Icon, Input } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { RenderFields, RestrictedBanner } from '~/components';
import { BankAccountDropdown } from '~/components/fields/BankAccountDropdown';
import { LoanDropdown } from '~/components/fields/LoanDropdown';
import { useNavigate } from '~/lib/navigation';
import { LoanRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, Inner } from '~/styles';
import { CreateLoanPaymentRequest } from '~/typings/API';

type CreateLoanPaymentRequestForm = Omit<
  CreateLoanPaymentRequest,
  'principleBalance' | 'principalAmount' | 'amount'
> & {
  principalAmount: {
    amount: string;
    currency: string;
  };
  amount: {
    amount: string;
    currency: string;
  };
};

interface Params {
  id?: string;
}

export const PageLoansPaymentCreate: React.FC = () => {
  const currentPermission = useSessionStore((state) => state.currentPermission);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;

  const formMethods = useForm<CreateLoanPaymentRequestForm>({
    defaultValues: {
      principalAmount: { amount: '', currency: '' },
      amount: { amount: '', currency: '' },
      loanId: id,
    },
  });

  const { handleSubmit } = formMethods;

  const handleCreate = (data: CreateLoanPaymentRequestForm) => {
    const currency = data.principalAmount?.currency || data.amount?.currency || 'USD';

    LoanRepository.createPayment({
      description: data.description,
      loanId: data.loanId,
      bankAccountId: data.bankAccountId,
      principalAmount: data.principalAmount?.amount,
      amount: data.amount?.amount,
      currency,
    })
      .then((response) => {
        navigate(`${ROUTE.LOANS}/edit/${data.loanId}`);
        addSuccessNotification({
          content: `${response.description} added`,
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
        });
      });
  };

  return (
    <>
      <PageActions>
        <Button
          onClick={() => navigate(id ? `${ROUTE.LOANS}/edit/${id}` : `${ROUTE.LOANS}/payments`)}
          variant="secondary"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleCreate)}
          isDisabled={currentPermission?.loans !== 'write'}
          size="small"
          icon={<Icon.Plus />}
        >
          Create Payment
        </Button>
      </PageActions>

      {currentPermission?.loans !== 'write' && (
        <Inner pb={0}>
          <RestrictedBanner />
        </Inner>
      )}
      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleCreate)}>
          {RenderFields<CreateLoanPaymentRequestForm>({
            sections: [
              {
                headline: 'Information',
                fields: [
                  {
                    id: 'description',
                    label: 'Description',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <Input
                          value={value !== undefined ? String(value) : ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="Description"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'loanId',
                    label: 'Loan',
                    rules: { required: true },
                    children: ({ value, onChange }) => {
                      const loanId = typeof value === 'string' ? value : undefined;
                      return (
                        <LoanDropdown fullWidth maxWidth="640px" value={loanId} onChange={onChange} isDisabled={!!id} />
                      );
                    },
                  },
                  {
                    id: 'bankAccountId',
                    label: 'Bank Account',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { isTouched, error }) => {
                      const bankAccountId = typeof value === 'string' ? value : undefined;
                      return (
                        <BankAccountDropdown
                          value={bankAccountId}
                          onChange={onChange}
                          fullWidth
                          maxWidth="640px"
                          onOpenChange={(open) => !open && onBlur()}
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'principalAmount',
                    label: 'Principal Amount',
                    rules: {
                      required: true,
                      validate: (value) =>
                        (value as CreateLoanPaymentRequestForm['principalAmount']).amount !== undefined,
                    },
                    children: ({ value, onChange }) => {
                      const { amount, currency } = value as CreateLoanPaymentRequestForm['principalAmount'];
                      return (
                        <AmountInput
                          value={amount ? parseInt(String(amount), 10) : undefined}
                          onChange={(newAmount) => onChange({ amount: newAmount, currency })}
                          onCurrencyCodeChange={(newCurrency) => onChange({ currency: newCurrency, amount: amount })}
                          currencyCode={currency}
                          currencyList={CurrenciesUSD}
                        />
                      );
                    },
                  },
                  {
                    id: 'amount',
                    label: 'Amount',
                    rules: {
                      required: true,
                      validate: (value) => (value as CreateLoanPaymentRequestForm['amount']).amount !== undefined,
                    },
                    children: ({ value, onChange }) => {
                      const { amount, currency } = value as CreateLoanPaymentRequestForm['amount'];
                      return (
                        <AmountInput
                          value={amount ? parseInt(String(amount), 10) : undefined}
                          onChange={(newAmount) => onChange({ amount: newAmount, currency })}
                          onCurrencyCodeChange={(newCurrency) => onChange({ currency: newCurrency, amount: amount })}
                          currencyCode={currency}
                          currencyList={CurrenciesUSD}
                        />
                      );
                    },
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <Inner>
        <EditToolbar>
          <Button
            onClick={() => navigate(id ? `${ROUTE.LOANS}/edit/${id}` : `${ROUTE.LOANS}/payments`)}
            variant="secondary"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleCreate)}
            isDisabled={currentPermission?.loans !== 'write'}
            size="small"
            icon={<Icon.Plus />}
          >
            Create Payment
          </Button>
        </EditToolbar>
      </Inner>
    </>
  );
};
